/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "@i18n";
import { fileViewModes } from "./constants";
import useFileManagerShared from "./useFileManagerShared";

interface UseFileManagerFilesProps {
  setSelectedFolder: Function;
  onFileClick?: Function;
  onFileSelect?: Function;
  fileManagerData?: any;
  contextMenuSettings?: any;
  menuOpen?: Function;
  fileColumns?: any;
  tempFolder?: any;
  handleSearchFiles?: Function;
  onFileLoad?: Function;
  beforeSending?: Function;
  allowDragAndDropUpload?: boolean;
  selectedFolder?: any;
  filesViewMode?: string;
  setSelectedFilesInIconsMode?: Function;
  selectedFilesInIconsMode?: any[];
  setIsViewRendered?: Function;
  isViewRendered?: boolean;
  disableDragAndDropUpload?: boolean;
  preSearchText?: string;
}

function useFileManagerFiles({
  setSelectedFolder,
  onFileClick = () => {},
  onFileSelect = () => {},
  fileManagerData = {},
  contextMenuSettings,
  menuOpen = () => [],
  fileColumns = [],
  tempFolder = {},
  handleSearchFiles = () => {},
  onFileLoad = () => {},
  beforeSending = () => {},
  allowDragAndDropUpload = false,
  selectedFolder = {},
  filesViewMode = "DETAILED_FILES",
  setSelectedFilesInIconsMode = () => {},
  selectedFilesInIconsMode = [],
  setIsViewRendered = () => {},
  isViewRendered: _isViewRendered = false,
  disableDragAndDropUpload = false,
  preSearchText = "",
}: UseFileManagerFilesProps) {
  const { t } = useTranslation();

  // Use shared hook for platform-agnostic logic (breadcrumbs, search, file filtering)
  const {
    query,
    setQuery,
    debounced,
    setDebounced,
    showPathColumn,
    setShowPathColumn,
    filesData,
    setFilesData,
    breadCrumbData,
    setBreadCrumbData,
    isFirstRender,
    setIsFirstRender,
  } = useFileManagerShared({
    setSelectedFolder,
    fileManagerData,
    tempFolder,
    handleSearchFiles,
    selectedFolder,
    preSearchText,
    onFileLoad,
  });

  // Web-specific state
  const [disabledRows, setDisabledRows] = useState<any>([]);
  const [columnDefinitions, setColumnDefinitions] = useState(fileColumns);
  const [menuState, setMenuState] = useState<any>({
    open: false,
    source: "",
    position: null,
    data: null,
    options: null,
  });
  const [isOver, setIsOver] = useState(false);

  // Refs
  const contextMenuSettingsRef = useRef<any>(null);
  const isMounted = useRef(false);
  const dragCounter = useRef(0);
  const searchRef = useRef<any>(null);
  const tempRef = useRef<any>({});

  // Handlers
  const setSelectedRows = (indexes: any, dataGrid: any) => {
    if (Array.isArray(indexes)) {
      if (filesViewMode === fileViewModes.DETAILED_VIEW) {
        dataGrid.current?.slickGrid?.setSelectedRows(indexes);
      } else if (filesViewMode === fileViewModes.LARGE_ICONS_VIEW) {
        if (indexes?.length == 0) {
          setSelectedFilesInIconsMode([]);
        } else {
          const filesToSelect: any = [];
          indexes.forEach((index: number) => {
            if (filesData[index]) {
              filesToSelect.push(filesData[index]);
            }
          });
          setSelectedFilesInIconsMode(filesToSelect);
        }
      }
    }
  };

  const recordClick = (clickedRow: any) => {
    if (clickedRow.resourceType === "RESOURCE_TYPE_FOLDER" || clickedRow.resourceType === "RESOURCE_TYPE_TRANSMITTAL") {
      setSelectedFolder(clickedRow);
    }
    onFileClick(clickedRow);
  };

  const selectionHandlerSlickGrid = (dataGrid: any) => {
    const selectedData = dataGrid.current?.slickGrid
      ?.getSelectedRows()
      .map((data: any) => dataGrid.current.slickGrid.getDataItem(data));
    const filteredData = selectedData?.filter(Boolean) || [];
    onFileSelect(filteredData);
  };

  const searchFileHandler = (args: React.ChangeEvent<HTMLInputElement>) => {
    const value = args.target.value?.toLowerCase() || "";
    setQuery(value);
  };

  const handleClose = () => {
    setMenuState((s: any) => ({ ...s, source: "", open: false, position: null }));
  };

  const handleContextMenuForResource = (e: React.MouseEvent, file: any, selectedFiles: any) => {
    let options = ["RESOURCE_TYPE_FOLDER", "RESOURCE_TYPE_TRANSMITTAL"].includes(file.resourceType)
      ? contextMenuSettingsRef.current?.folder
      : contextMenuSettingsRef.current?.file;

    // Options may declare an `extensions` allow-list (e.g. Export to PDF for dwg/dxf only).
    // Items without `extensions` always show; items with it show only for a matching file.
    const fileExt = (file?.name?.split(".").pop() || "").toLowerCase();
    options = options?.filter((opt: any) => !opt.extensions || opt.extensions.includes(fileExt));

    const disabledItems = menuOpen(selectedFiles);
    options = options?.map((opt: any) => {
      opt.disabled = false;
      if (disabledItems?.includes(opt.id)) {
        opt.disabled = true;
      }
      return opt;
    });
    setMenuState({
      options,
      source: file.resourceType === "RESOURCE_TYPE_FOLDER" ? "FOLDER" : "FILE",
      open: contextMenuSettingsRef.current?.visible,
      position: {
        top: e.pageY,
        left: e.pageX,
      },
      data: file,
    });
  };

  const contextMenuForIconsView = (e: React.MouseEvent, file: any) => {
    e.stopPropagation();
    e.preventDefault();
    const selectedFiles = [...selectedFilesInIconsMode];
    setSelectedFilesInIconsMode((prev: any) => [...prev, file]);
    handleContextMenuForResource(e, file, selectedFiles);
  };

  const handleGridCreated = (reactGrid: any, _dataGrid: any) => {
    setIsViewRendered(true);
    reactGrid.slickGrid.onContextMenu.subscribe((e: any) => {
      e.preventDefault();
      const cell = reactGrid.slickGrid.getCellFromEvent(e);
      if (!cell) return;

      const row = cell.row;
      const dataView = reactGrid.slickGrid.getData();
      const item = dataView.getItem(row);

      if (!reactGrid?.slickGrid?.getSelectedRows().includes(row)) {
        reactGrid.slickGrid.setSelectedRows([row]);
      }
      const selectedFiles = reactGrid.slickGrid
        .getSelectedRows()
        .map((data: any) => reactGrid.slickGrid.getDataItem(data));
      handleContextMenuForResource(e, item, selectedFiles);
    });
    reactGrid.slickGrid.onDblClick.subscribe((e: any) => {
      e.preventDefault();
      const cell = reactGrid.slickGrid.getCellFromEvent(e);
      if (!cell) return;

      const row = cell.row;
      const dataView = reactGrid.slickGrid.getData();
      const item = dataView.getItem(row);
      recordClick(item);
    });
    reactGrid.slickGrid.onScroll.subscribe(() => {
      setMenuState((s: any) => (s.open ? { ...s, source: "", open: false, position: null } : s));
    });
  };

  const onContextMenu = (args: React.MouseEvent) => {
    if (!(args.target as HTMLElement).closest(".slick-row")) {
      if (menuState.position) {
        return;
      }
      args.preventDefault();
      let options = contextMenuSettings?.layout || [];
      const disabledItems = menuOpen();
      options = options.map((opt: any) => {
        opt.disabled = false;
        if (disabledItems?.includes(opt.id)) {
          opt.disabled = true;
        }
        return opt;
      });
      setMenuState({
        options,
        source: "LAYOUT",
        open: contextMenuSettings?.visible,
        position: {
          top: args.pageY,
          left: args.pageX,
        },
      });
    }
  };

  const isFileDrag = (e: React.DragEvent) => Array.from(e.dataTransfer?.types || []).includes("Files");

  const onDragEnter = (e: React.DragEvent<HTMLElement>) => {
    if (!isFileDrag(e) || !allowDragAndDropUpload) return;
    e.preventDefault();
    dragCounter.current++;
    setIsOver(true);
  };

  const onDragLeave = (e: React.DragEvent<HTMLElement>) => {
    if (!isFileDrag(e) || !allowDragAndDropUpload) return;
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) {
      setIsOver(false);
    }
  };

  const onDragOver = (e: React.DragEvent<HTMLElement>) => {
    if (!isFileDrag(e) || !allowDragAndDropUpload) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = (e: React.DragEvent<HTMLElement>) => {
    if (!isFileDrag(e) || !allowDragAndDropUpload) return;
    e.preventDefault();
    dragCounter.current = 0;
    setIsOver(false);
    beforeSending({
      action: "upload",
      files: e.dataTransfer.files,
    });
  };

  const handleMenuClick = (item: any, dataGrid: any) => {
    let selectedData: any[] = [];
    if (filesViewMode == fileViewModes.DETAILED_VIEW) {
      selectedData = dataGrid.current?.slickGrid
        ?.getSelectedRows()
        .map((data: any) => dataGrid.current.slickGrid.getDataItem(data));
    } else if (filesViewMode === fileViewModes.LARGE_ICONS_VIEW) {
      selectedData = selectedFilesInIconsMode;
    }

    if (item.id == "cut") {
      const ids = selectedData.map((file: any) => file.id);
      setDisabledRows(ids);
    } else if (item.id == "paste") {
      setDisabledRows([]);
    } else if (
      item.id == "open" &&
      (selectedData[0]?.resourceType === "RESOURCE_TYPE_FOLDER" ||
        selectedData[0]?.resourceType === "RESOURCE_TYPE_TRANSMITTAL")
    ) {
      setSelectedFolder(selectedData[0]);
    }
    if (item.onClick) {
      item.onClick(selectedData);
    }
    setMenuState((s: any) => ({ ...s, source: "", open: false, position: null }));
  };

  const checkIconBoxSelect = (args: React.ChangeEvent<HTMLInputElement>, file: any) => {
    let files = [...selectedFilesInIconsMode];
    if (args.target.checked) {
      files.push(file);
      setSelectedFilesInIconsMode([...files]);
    } else {
      files = files.filter((item) => item.id != file.id);
      setSelectedFilesInIconsMode([...files]);
    }
  };

  // Web-specific effects (shared logic is in useFileManagerShared)
  useEffect(() => {
    contextMenuSettingsRef.current = contextMenuSettings;
  }, [contextMenuSettings]);

  // Clear web search input when folder changes
  useEffect(() => {
    const searchEle: any = document.getElementById("fileSearchBox");
    if (searchEle) {
      searchEle.value = null;
    }
  }, [selectedFolder?.id]);

  const fileColumnsKey = (fileColumns || []).map((c: any) => c?.id).join("|");

  useEffect(() => {
    setColumnDefinitions(() => [
      ...fileColumns,
      ...(showPathColumn
        ? [
            {
              id: "path",
              field: "path",
              name: "Path",
              formatter: (data: number, params: number, a: any, b: unknown, value: any) =>
                value.path ? `/${value.path.join("/")}` : "/",
              minWidth: 200,
            },
          ]
        : []),
    ]);
  }, [showPathColumn, fileColumnsKey]);

  useEffect(() => {
    if (isMounted.current) {
      onFileSelect(selectedFilesInIconsMode);
    } else {
      isMounted.current = true;
    }
  }, [selectedFilesInIconsMode?.length]);

  return {
    // State
    disabledRows,
    setDisabledRows,
    breadCrumbData,
    setBreadCrumbData,
    filesData,
    setFilesData,
    showPathColumn,
    setShowPathColumn,
    isFirstRender,
    setIsFirstRender,
    columnDefinitions,
    setColumnDefinitions,
    menuState,
    setMenuState,
    debounced,
    setDebounced,
    query,
    setQuery,
    isOver,
    setIsOver,

    // Refs
    contextMenuSettingsRef,
    isMounted,
    dragCounter,
    searchRef,
    tempRef,

    // Handlers
    setSelectedRows,
    recordClick,
    selectionHandlerSlickGrid,
    searchFileHandler,
    handleClose,
    handleContextMenuForResource,
    contextMenuForIconsView,
    handleGridCreated,
    onContextMenu,
    isFileDrag,
    onDragEnter,
    onDragLeave,
    onDragOver,
    handleDrop,
    handleMenuClick,
    checkIconBoxSelect,

    // Translation
    t,

    // Props passthrough
    selectedFolder,
    filesViewMode,
    selectedFilesInIconsMode,
    disableDragAndDropUpload,
    allowDragAndDropUpload,
  };
}

export default useFileManagerFiles;
